import {BaseService} from "@/api/services/baseService";
import request, {shippingRequestHttp} from "@/api";
import type {
    DeliveryInfo,
    TransportOrderAndRequirementIdVo,
    TransportOrderShip,
    TransportOrderShipVo
} from "@zhihaoscm/entity";
import {confirmable} from "@/utils/decorator/confirmable";
import type {PageParams} from "@/entity/logistics/waybillManagement";

export class WaybillService extends BaseService<unknown, TransportOrderShipVo, PageParams, unknown> {
    @confirmable("是否完成，完成后将不能修改")
    public async complete(id: number | string) {
        return this.service.put(`${this.path}/complete/${id}`);
    }

    @confirmable("是否关闭，关闭后该船运单将不再展示")
    public async closeShipOrder(id: number | string) {
        return this.service.delete(`${this.path}/${id}`);
    }
    // 获取专员列表
    public async getSpecials(serviceSpecialsType?: number | string): Promise<unknown> {
        const { data } = await this.service.get(
            `${this.path}/find/specials?serviceSpecialsType=${serviceSpecialsType}`
        );
        return data;
    }

    // 获取专员下拉数据
    public async getSelectSpecials(): Promise<unknown> {
        const { data } = await this.service.get(`${this.path}/find/specials`);
        return data;
    }
    // 服务运单-指派
    public async assign(
        id: string,
        params: { upstreamHandlerId: string; downstreamHandlerId: string }
    ): Promise<unknown> {
        const { data } = await this.service.put(
            `${this.path}/assign/${id}?${
                params.upstreamHandlerId ? `upstreamHandlerId=${params.upstreamHandlerId}` : ""
            }${params.downstreamHandlerId ? `&downstreamHandlerId=${params.downstreamHandlerId}` : ""}`
        );
        return data;
    }
    // 撮合运单-指派
    public async matchAssign(id: string, handlerId: string): Promise<unknown> {
        const { data } = await shippingRequestHttp.service.get(`${this.path}/assign/${id}/${handlerId}`);
        return data;
    }
    // 开始装货
    public async startLoad(id: number | string, form: DeliveryInfo): Promise<unknown> {
        const { data } = await this.service.put(`${this.path}/start-loading/${id}`, form);
        return data;
    }
    // 发航申请
    @confirmable("是否确认，确认后货主端显示离港准备信息")
    public async departApplication(id: number | string): Promise<unknown> {
        const { data } = await this.service.put(`${this.path}/flight-application/${id}`);
        return data;
    }
    @confirmable("是否确认，确保已经完成卸货")
    public async confirmUnloading(id: number | string) {
        const { data } = await this.service.put(`${this.path}/confirm-unloading/${id}`);
        return data;
    }
    public async getSelector(params: {
        isSrcId?: boolean;
        isGoodsId?: boolean;
        keyword: string;
        excludeScheduled?: boolean; //是否排除已关联的排期的船运单 true代表排除
        transportOrderId?: string; // 当前数据持有的船运单id
    }): Promise<TransportOrderShip[]> {
        const { data } = await this.service.get(`${this.path}/selector`, { params });
        return data;
    }

    /**
     * 关联船运单 / 船运需求 远程下拉（下拉船运单 or 需求）
     * `GET /admin/transport/order/ship/selector/transportOrder/requirement`
     */
    public async getSelectorTransportOrderRequirement(params: {
        keyword: string;
    }): Promise<TransportOrderAndRequirementIdVo[]> {
        const { data } = await this.service.get(`${this.path}/selector/transportOrder/requirement`, { params });
        return data ?? [];
    }
    /**
     * 绑定
     * @param {string} id 关联船运单id
     * @param {string} srcId 货主找船需求id
     * @param {string} goodsId 提货单id
     * @returns
     */
    public async bind(id: string, srcId?: string, goodsId?: string) {
        const urlArr = [];
        if (srcId) urlArr.push("srcId=" + srcId);
        if (goodsId) urlArr.push("goodsId" + goodsId);
        const url = urlArr.join("&");
        await this.service.put(`${this.path}/bind/${id}?${url}`);
    }

    /**
     * 船务交易进去船运单详情
     */
    public async getDetail(id: string): Promise<TransportOrderShipVo> {
        const { data } = await this.service.get(`${this.path}/detail/${id}`);
        return data;
    }

    // 管理后台代货主同意卸货
    @confirmable("是否要帮货主同意卸货", "同意卸货", "已确认")
    public async agreeUnload(id: string | number): Promise<unknown> {
        const { data } = await this.service.put(`${this.path}/agree-unload/${id}`, {});
        return data;
    }

    // 管理后台代船主到港确认
    public async confirmationArrivalPort(id: string | number, fileId: number): Promise<unknown> {
        const { data } = await this.service.put(
            `${this.path}/confirmation-arrival-port/${id}?arrivalVideoId=${fileId}`
        );
        return data;
    }
    public async confirmationDeparture(
        id: string | number,
        params: { drainage: number; freightSettlementTonnage: number }
    ): Promise<unknown> {
        const { data } = await shippingRequestHttp.service.put(`${this.path}/confirmation-departure/${id}`, params);
        return data;
    }
    // 上传文件（水印）
    public async uploadWatermark(id: string, params: FormData): Promise<unknown> {
        const { data } = await this.service.post(`${this.path}/upload/watermark/${id}`, params);
        return data;
    }

    public async createShipOrder(form: unknown): Promise<unknown> {
        const { data } = await shippingRequestHttp.service.post(this.path, form);
        return data;
    }
    // 修改船运单
    public async updateShipOrder(id: string, form: unknown): Promise<unknown> {
        const { data } = await this.service.post(`${this.path}/${id}`, form);
        return data;
    }
    // 创建撮合船运单
    public async createMatchShipOrder(form: unknown): Promise<unknown> {
        const { data } = await shippingRequestHttp.service.post(`${this.path}/match/create`, form);
        return data;
    }

    // 到港审核
    public async arrivalReview(id: string, params: { arrivalPortReviewState: number, reviewReason: string }) {
        const { data } = await shippingRequestHttp.service.post(`${this.path}/arrival-port/review/${id}`, { ...params });
        return data;
    }
    // 修改目的地
    public async putUpdateDestination(id: number | string, destinationPortId: number | string): Promise<unknown> {
        const { data } = await shippingRequestHttp.service.putForm(`${this.path}/update-destination/${id}`, {
            destinationPortId
        });
        return data;
    }
}

export const waybillService = new WaybillService("/admin/transport/order/ship", request.service);

export class WaybillDetailService extends BaseService<unknown, unknown, unknown, unknown> {
    // 修改装货信息
    public async updateDelivery(id: number | string, form: DeliveryInfo): Promise<unknown> {
        const { data } = await this.service.put(`${this.path}/update/delivery/${id}`, form);
        return data;
    }

    // 修改接档记录
    public async updateGearShift(id: number | string, form: unknown): Promise<unknown> {
        const { data } = await this.service.put(`${this.path}/update/gear-shift/${id}`, form);
        return data;
    }

    public async updateDeparturePreparation(id: number | string, form: unknown): Promise<unknown> {
        const { data } = await shippingRequestHttp.service.put(
            `/transport/order/details/ship/update/departure-preparation/${id}`,
            form
        );
        return data;
    }
    // 修改发航记录
    public async updateSetSail(id: number | string, form: unknown): Promise<unknown> {
        const { data } = await this.service.put(`${this.path}/update/set-sail/${id}`, form);
        return data;
    }
    public async updateUnloading(id: number | string, form: unknown): Promise<unknown> {
        const { data } = await shippingRequestHttp.service.put(
            `/transport/order/details/ship/update/unloading/${id}`,
            form
        );
        return data;
    }
    // 管理后台代船主上传排水视频
    public async uploadDrainageVideo(id: string | number, fileId: number): Promise<unknown> {
        const { data } = await shippingRequestHttp.service.put(
            `/transport/order/details/ship/upload-drainage-video/${id}`,
            {
                drainageVideoId: fileId
            }
        );
        return data;
    }
    public async uploadDrainageInfo(id: string, params: { loadVideoId: number; tonProof?: number }): Promise<unknown> {
        const { data } = await shippingRequestHttp.service.put(
            `/transport/order/details/ship/update/departure-information/${id}`,
            params
        );
        return data;
    }
    public async uploadUnloadingInfo(id: string, params: { unloadingVideoId: number }): Promise<unknown> {
        const { data } = await shippingRequestHttp.service.put(
            `/transport/order/details/ship/update/unloading-information/${id}`,
            params
        );
        return data;
    }
}

export const waybillDetailService = new WaybillDetailService("/admin/transport/order/details/ship", request.service);
/**
 * 船运单列表
 */
export const getTransportOrderShipPaging = async (params: PageParams): Promise<unknown> => {
    const { data } = await shippingRequestHttp.service.get(`/transport/order/ship/paging`, { params });
    return data;
};
// 获取关联订单号
export const getOrderId = async (params: { id: number | string }): Promise<unknown> => {
    const { data } = await request.service.get(`/transport/order/ship/find-orderId`, { params });
    return data;
};

// 查询船运单操作提醒响应接口
/**
 * 查询船运单操作提醒
 * @param {string} id
 * @returns
 */
export const findOperateWarn = async (id: string): Promise<unknown> => {
    const { data } = await shippingRequestHttp.service.get(`/transport/order/ship/find/operate-warn/${id}`);
    return data;
};
